import moment from 'moment';
import { checkPhoneValidation } from '@components/PixelFormComponents/PhoneInputHandler/Helpers';

const isFilled = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

export const methods = [
  {
    name: 'mobile',
    register: (yup) => {
      yup.addMethod(yup.string, 'mobile', function (message = 'Must be a valid phone number') {
        return this.test('mobile', message, (value) => {
          if (!value) return true;
          return checkPhoneValidation(value);
        });
      });
    }
  },
  {
    name: 'validArrayObjects', // means that the array must contain at least one valid object
    register: (yup) => {
      yup.addMethod(yup.array, 'validArrayObjects', function (message = 'Array must contain at least one valid object') {
        return this.test('valid-array-objects', message, (value) => {
          if(!value) return true;
          if(!Array.isArray(value) || value.length === 0) return false;
          return value.some(item => Object.values(item)?.length > 0);
        });
      });
    }
  },
  {
    name: 'duplicatedKeys', // means that the array must not contain duplicate rows with keys you will pass to the method
    register: (yup) => {
      yup.addMethod(yup.array, 'duplicatedKeys', function (fields = [], message = 'Duplicate rows are not allowed') {
        return this.test('duplicated-keys', message, (rows) => {
          if (!rows?.length) return true;
          const keys = rows.map(r => fields.map(f => r?.[f] ?? '').join('|'));
          return keys.length === new Set(keys).size;
        });
      });
    }
  },
  {
    name: 'date', // validates a string matches a moment format (strict parsing)
    register: (yup) => {
      yup.addMethod(yup.string, 'date', function (format, message) {
        return this.test('date', message || `Must match format ${format}`, (value) => {
          if (!value) return true;
          return moment(value, format, true).isValid();
        });
      });
    }
  },
  {
    name: 'requiredWithout', // required unless ctx.button_value matches the supplied value
    register: (yup) => {
      yup.addMethod(yup.mixed, 'requiredWithout', function (buttonValue, message = 'This field is required') {
        return this.nullable().test('required-without', message, function (value) {
          const ctx = this.options.context || {};
          if (ctx.button_value === buttonValue) return true;
          return isFilled(value);
        });
      });
    }
  },
  {
    name: 'requiredWithUnless', // required when any sibling is filled unless button_value matches
    register: (yup) => {
      yup.addMethod(yup.mixed, 'requiredWithUnless', function (excludedButtonValue, siblingFields = [], message = 'This field is required') {
        return this.nullable().test('required-with-unless', message, function (value) {
          const ctx = this.options.context || {};
          if (ctx.button_value === excludedButtonValue) return true;

          const siblings = this.parent || {};
          const hasFilledSibling = siblingFields.some(field => isFilled(siblings?.[field]));

          return !hasFilledSibling || isFilled(value);
        });
      });
    }
  }
];

/*
    *Methods exported as array of objects => {name , factory}
        -name => string
        -factory => function to return the method

    *usage
        yup.string().mobile('message').required() => similar to normal mode .
        yup.array().validArrayObjects('message').required() => similar to normal mode . => check if objects in array has values && you will check every field inside

    *Button-aware required method (mirrors backend's required_without pattern):
        - relies on FormHandler injecting `button_value` into the yup validation context
          (driven by the saveButtonValue/submitButtonValue props on FormActions)
        - pass the button value that should skip the required check
        - for conditional behavior (only required when another field has a certain value),
          wrap with native yup .when():

        yup.string().requiredWithout('save_draft', 'msg')
            => required unless the user clicked the Save (draft) button
            (equivalent to backend `required_without:BaseModel::BUTTON_VALUE_SAVE_DRAFT`)

        yup.array().of(yup.number()).when('incident_category', {
          is: 'personal',
          then: s => s.requiredWithout('save_draft', 'msg'),
          otherwise: s => s.notRequired(),
        })
            => required unless save_draft, AND only when category is personal
            (equivalent to backend `required_without:BaseModel::BUTTON_VALUE_SAVE_DRAFT,incident_category,personal`)

    *Date format method (moment strict parsing):
        yup.string().date('hh:mm A', 'Time must match format hh:mm AM/PM')
            => validates the string is parseable by moment with the given format
*/
